import React from "react";
import {
  AppBar,
  Button,
  Container,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import moment from "moment";
import { useSelector } from "react-redux";
import { DataGrid, GridToolbarContainer, GridToolbar } from "@mui/x-data-grid";
import API from "../../Api/api";
import toast from "react-hot-toast";
import { endOfMonth, startOfMonth } from "../../ui/today";
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
});

const columns = [
  {
    field: "id",
    headerName: "Claim Code",
    minWidth: 250,
  },
  {
    field: "date",
    headerName: "Date",
    minWidth: 170,
    align: "left",
  },
  {
    field: "branch",
    headerName: "Branch",
    minWidth: 170,
    align: "left",
  },
  {
    field: "category",
    headerName: "category",
    minWidth: 170,
    align: "left",
  },
  {
    field: "designation",
    headerName: "designation",
    minWidth: 170,
    align: "left",
  },
  {
    field: "department",
    headerName: "Department",
    minWidth: 170,
    align: "left",
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 170,
    align: "left",
  },
  {
    field: "employee_name",
    headerName: "Employee Name",
    minWidth: 170,
    align: "left",
  },

  {
    field: "amount",
    headerName: "Amount",
    minWidth: 170,
    align: "left",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "sanctioned_by",
    headerName: "Sannctioned By",
    minWidth: 170,
    align: "left",
  },
  {
    field: "approved_by",
    headerName: "Approved By",
    minWidth: 170,
    align: "left",
  },
  {
    field: "attention_to",
    headerName: "Attention To",
    minWidth: 170,
    align: "left",
  },
  {
    field: "payment_status",
    headerName: "Payment Status",
    minWidth: 170,
    align: "left",
  },
];

function createData(
  id,
  date,
  branch,
  category,
  designation,
  department,
  description,
  employee_name,
  amount,
  sanctioned_by,
  approved_by,
  attention_to,
  payment_status
) {
  return {
    id,
    date,
    branch,
    category,
    designation,
    department,
    description,
    employee_name,
    amount,
    sanctioned_by,
    approved_by,
    attention_to,
    payment_status,
  };
}
function CustomToolbar() {
  return (
    <GridToolbarContainer
      style={{ backgroundColor: "#75CA93 ", color: "white" }}>
      <GridToolbar style={{ color: "white" }} />
    </GridToolbarContainer>
  );
}
export default function ClaimReport() {
  const [showingData, setShowingData] = React.useState("data");
  const departments = useSelector((state) => state.departments.depart);
  const category = useSelector((state) => state.category.cart);
  const locations = useSelector((state) => state.locations);
  const approvedClaims = useSelector((state) => state.approved);
  const [approved, setApproved] = React.useState(approvedClaims);
  const [queryData, setQueryData] = React.useState({
    branch: "",
    category: "",
    department: "",
    claim_start: "",
    claim_end: "",
    designation: "",
  });

  React.useEffect(() => {
    // Function to check if all fields in queryData are empty
    const isQueryDataEmpty = Object.values(queryData).every(
      (value) => value === ""
    );

    // Only proceed if all fields in queryData are empty
    if (isQueryDataEmpty) {
      if (showingData === "month") {
        const data = approvedClaims.filter(
          (d) =>
            moment(d.date) >= moment(startOfMonth) &&
            moment(d.date) < moment(endOfMonth)
        );
        setApproved(data);
      } else {
        setApproved(approvedClaims);
      }
    }
  }, [showingData, approvedClaims, queryData]);

  const branch = queryData.branch
    ? `&branch[eq]=${queryData.branch.toLowerCase()}`
    : "";
  const categoryQuery = queryData.category
    ? `&category[eq]=${queryData.category}`
    : "";
  const designationQuery = queryData.designation
    ? `&designation[eq]=${queryData.designation}`
    : "";
  const departmentQuery = queryData.department
    ? `&department[eq]=${queryData.department}`
    : "";
  const claimStartQuery = queryData.claim_start
    ? `&date[gte]=${queryData.claim_start}`
    : "";
  const claimEndQuery = queryData.claim_end
    ? `&date[lte]=${queryData.claim_end}`
    : "";
  const query = `/api/v1/eClaim?status[eq]=approved${branch}${categoryQuery}${departmentQuery}${claimStartQuery}${designationQuery}${claimEndQuery}`;
  const fetchData = async () => {
    let fetchingId;
    try {
      fetchingId = toast.loading("Fetching data...");
      const res = await API.get(query);
      if (res.data) {
        setApproved(res.data);
      }
      toast.remove(fetchingId);
      toast.success("Data Loaded");
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
      setApproved(approvedClaims);
    }
  };

  function clearFilter() {
    if (showingData === "month") {
      const data = approvedClaims.filter(
        (d) =>
          moment(d.date) >= moment(startOfMonth) &&
          moment(d.date) < moment(endOfMonth)
      );
      setApproved(data);
      setQueryData({
        branch: "",
        category: "",
        department: "",
        claim_start: "",
        claim_end: "",
        designation: "",
      });
      return;
    }
    setQueryData({
      branch: "",
      category: "",
      department: "",
      claim_start: "",
      claim_end: "",
      designation: "",
    });
    setApproved(approvedClaims);
  }

  const rows = approved
    .filter((d) => d.status === "approved")
    .map((data) =>
      createData(
        data.claim_code,
        moment(data.date).format("DD-MM-YYYY"),
        data.branch,
        data.category,
        data.designation || "staff",
        data.department,
        data.description,
        data.employee_name,
        data.amount,
        data.sanctioned_by,
        data.approved_by,
        data.attention_to,
        data.payment_status
      )
    );
  const totalAmount = approved.reduce((acc, obj) => acc + obj.amount, 0);
  const formattedAmount = totalAmount.toLocaleString();

  return (
    <Paper>
      <Container>
        <AppBar
          position='fixed'
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
            flexGrow: 1,
            marginTop: "60px",
            width: "95%",
            backgroundColor: "#ccc",
            color: "#000",
          }}>
          <div
            elevation={16}
            style={{
              fontSize: "14px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}>
            <div
              style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Branch
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.branch}
                  onChange={(e) =>
                    setQueryData({ ...queryData, branch: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {locations?.map((data) => {
                    return (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Department
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.department}
                  onChange={(e) =>
                    setQueryData({ ...queryData, department: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {departments.map((data) => (
                    <MenuItem key={data._id} value={data.department}>
                      {data.department}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Category
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.category}
                  onChange={(e) =>
                    setQueryData({ ...queryData, category: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {category.map((data) => (
                    <MenuItem key={data._id} value={data.category}>
                      {data.category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Designation
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.designation}
                  onChange={(e) =>
                    setQueryData({ ...queryData, designation: e.target.value })
                  }
                  label='Designation'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>

                  <MenuItem value='staff'>staff</MenuItem>
                  <MenuItem value='non-staff'>non-staff</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#116149",
                }}
                htmlFor='acq-start-date'>
                Claim start date
              </label>
              <input
                value={queryData.claim_start}
                onChange={(e) =>
                  setQueryData({ ...queryData, claim_start: e.target.value })
                }
                style={{
                  // alignItems: "center",
                  alignText: "center",

                  fontSize: "16px",

                  borderRadius: "5px",
                  // backgroundColor: "#cccc",
                  border: "1px solid #3D4F58",
                }}
                id='acq-end-date'
                type='date'
                size='small'
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "5px",
              }}>
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#116149",
                }}
                htmlFor='acq-start-date'>
                Claim end date
              </label>
              <input
                value={queryData.claim_end}
                onChange={(e) =>
                  setQueryData({ ...queryData, claim_end: e.target.value })
                }
                style={{
                  // alignItems: "center",
                  alignText: "center",

                  fontSize: "16px",

                  borderRadius: "5px",
                  // backgroundColor: "#cccc",
                  border: "1px solid #3D4F58",
                }}
                id='acq-end-date'
                type='date'
                size='small'
              />
            </div>
          </div>

          <div
            style={{
              // marginTop: "-35px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              columnGap: "10px",
            }}>
            <div>
              <FormControl
                size='small'
                disabled={
                  queryData.branch ||
                  queryData.category ||
                  queryData.designation ||
                  queryData.claim_end ||
                  queryData.claim_start
                }>
                <FormLabel
                  style={{ fontSize: "12px" }}
                  id='demo-row-radio-buttons-group-label'>
                  Show Data For
                </FormLabel>
                <RadioGroup
                  value={showingData}
                  onChange={(e) => setShowingData(e.target.value)}
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'>
                  <FormControlLabel
                    value='month'
                    control={<Radio size='small' />}
                    label='This Month'
                  />
                  <FormControlLabel
                    value='data'
                    control={<Radio size='small' />}
                    label='All Data'
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <Button
              onClick={fetchData}
              size='small'
              variant='contained'
              style={{ backgroundColor: "#75CA93" }}>
              Generate Report
            </Button>
            <Button
              onClick={clearFilter}
              size='small'
              variant='contained'
              style={{ backgroundColor: "#f47373" }}>
              Clear Filter
            </Button>
          </div>
        </AppBar>
      </Container>

      <Paper
        style={{
          marginTop: "10px",
        }}>
        <DataGrid
          density='standard'
          columns={columns}
          rows={rows}
          {...rows}
          slots={{ toolbar: CustomToolbar }}
          style={{ height: "60vh", fontSize: "12px" }}
        />
        <Typography>Total cost:{formattedAmount}</Typography>
      </Paper>
    </Paper>
  );
}
